/*----------------------------------------------------------------------------
    DateParts.ts

    holds parts of a DateTime. There is no timezone assumptions or information
    here.

    This is mostly (completely?) used in test support
----------------------------------------------------------------------------*/

import { RexDateTime } from "./RexDateTime";

export interface DateParts
{
    year: number;
    month: number;
    day: number;
    hour?: number;
    minute?: number;
    second?: number;
    msec?: number;
    dayOfWeek: number;
}

export class DateParts
{
    public static s_msecPerSecond = 1000;
    public static s_msecPerMinute = 60 * DateParts.s_msecPerSecond;
    public static s_msecPerHour = 60 * DateParts.s_msecPerMinute;

    public static getLocalDatePartsFromDate(date: Date, msecOffset: number): DateParts
    {
        const adjusted = new Date(date.valueOf() - msecOffset);

        return {
            year: adjusted.getFullYear(),
            month: adjusted.getMonth() + 1,
            day: adjusted.getDate(),
            hour: adjusted.getHours(),
            minute: adjusted.getMinutes(),
            second: adjusted.getSeconds(),
            msec: adjusted.getMilliseconds(),
            dayOfWeek: adjusted.getDay()
        };
    }

    public static getLocalDatePartsFromRexDateTime(date: RexDateTime, msecOffset: number): DateParts
    {
        return this.getLocalDatePartsFromDate(new Date(date.utcValue), msecOffset);
    }

    public static getLongDateStringFromParts(parts: DateParts)
    {
        const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

        return `${days[parts.dayOfWeek]}, ${months[parts.month - 1]} ${parts.day}, ${parts.year}`;
    }

    public static getDateStringFromParts(parts: DateParts)
    {
        return `${parts.year}-${parts.month.toString().padStart(2, '0')}-${parts.day.toString().padStart(2, '0')}`
    }

    public static getTimeStringFromParts(parts: DateParts)
    {
        return `${parts.hour!.toString().padStart(2, '0')}:${parts.minute!.toString().padStart(2, '0')}:${parts.second!.toString().padStart(2, '0')}`
    }

    public static getDateTimeStringFromParts(parts: DateParts)
    {
        return `${this.getDateStringFromParts(parts)} ${this.getTimeStringFromParts(parts)}`;
    }

    public static getLongDateTimeStringFromParts(parts: DateParts)
    {
        return `${this.getLongDateStringFromParts(parts)} ${this.getTimeStringFromParts(parts)}`;
    }

    public static msecOffsetForLocalTimezone(): number
    {
        const utc = new Date("2024-11-24@00:00:00Z");
        const local = new Date("2024-11-24@00:00:00");
        return utc.valueOf() - local.valueOf();
    }

    public static offsetStringFromMsecOffset(msecOffset: number): string
    {
        const isNegative = msecOffset < 0;
        msecOffset = Math.abs(msecOffset);

        const hours = Math.floor(msecOffset / this.s_msecPerHour);
        msecOffset -= hours * this.s_msecPerHour;

        const minutes = Math.floor(msecOffset / this.s_msecPerMinute);
        msecOffset -= minutes * this.s_msecPerMinute;

        if (msecOffset != 0)
            throw new Error(`offset is not an even hours/minutes. remainder: ${msecOffset}`);

        return `${isNegative ? "-" : "+"}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    public static offsetFromString(dateTimeString: string): { sign: number, hours: number, minutes: number } | null
    {
        const regex = /([+-])(\d{2}):(\d{2})$/;

        const match = dateTimeString.match(regex);

        if (!match)
            return null;

        return { sign: (match[1] == "-" ? -1 : 0), hours: parseInt(match[2], 10), minutes: parseInt(match[3], 10) };
    }

    public static msecOffsetFromString(dateTimeString: string): number | null
    {
        const offsetParts = this.offsetFromString(dateTimeString);

        if (offsetParts == null)
            return null;

        return offsetParts.hours * this.s_msecPerHour + offsetParts.minutes * this.s_msecPerMinute * offsetParts.sign;
    }
   
};